import { ref, onMounted, onUnmounted } from "vue";
import { CarClient } from "@/utils/capacitor/carPlugin";
import {
  CarPropertyValue,
  VehiclePropertyNames,
  VehiclePropertyName,
  VehicleInfoIds,
  VehicleInfoId,
} from "@/utils/capacitor/carPlugin";
import { isAndroid } from "@/utils/capacitor";
import { useDomainControlStore } from "@/stores/domainControl";

export function useCar(vehiclePropertyNames: VehiclePropertyName[] = []) {
  const isConnected = ref(false);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const vehicleProperties = ref<Record<string, any>>({});
  const unsubscribeFunctions: Record<string, () => Promise<void>> = {};
  const callbackIntervalSum: Record<string, number> = {};
  const callbackCounts: Record<string, number> = {};
  const lastCallbackTimestamps: Record<string, number> = {};

  const domainControlStore = useDomainControlStore();

  vehiclePropertyNames.forEach((property) => {
    vehicleProperties.value[property] = 0;
  });

  const checkConnection = async () => {
    if (!isAndroid) {
      return false;
    }
    try {
      const result = await CarClient.isConnected();
      isConnected.value = result.connected;
      return result.connected;
    } catch (err) {
      console.error("Failed to check car connection:", err);
      error.value = err instanceof Error ? err.message : String(err);
      return false;
    }
  };

  const calculateAndPrintAverageInterval = (
    propertyName: string,
    currentTimestamp: number
  ) => {
    const lastTimestamp = lastCallbackTimestamps[propertyName];
    if (lastTimestamp) {
      const interval = currentTimestamp - lastTimestamp;
      callbackIntervalSum[propertyName] += interval;
      callbackCounts[propertyName]++;

      if (
        domainControlStore.printInterval &&
        callbackCounts[propertyName] % 10 === 0
      ) {
        const avgInterval =
          callbackIntervalSum[propertyName] / callbackCounts[propertyName];
        console.log(
          `[${propertyName}] 调用计数: ${
            callbackCounts[propertyName]
          }, 平均间隔时间: ${avgInterval.toFixed(2)}ms, 最近间隔: ${interval}ms`
        );
      }
    }
    lastCallbackTimestamps[propertyName] = currentTimestamp;
  };

  const setupVehiclePropertyListeners = async () => {
    if (!isConnected.value || vehiclePropertyNames.length === 0) return;

    try {
      console.log("开始设置车辆属性监听器，属性列表:", vehiclePropertyNames);

      for (const propertyName of vehiclePropertyNames) {
        try {
          unsubscribeFunctions[propertyName] = await subscribeToProperty(
            propertyName,
            (data) => {
              vehicleProperties.value[propertyName] = data.value;
            }
          );
          console.log(`已设置 ${propertyName} 的监听器`);
        } catch (err) {
          console.error(`设置 ${propertyName} 监听器失败:`, err);
        }
      }

      console.log("车辆属性监听器设置完成");
    } catch (err) {
      console.error("设置车辆属性监听器失败:", err);
    }
  };

  const removeVehiclePropertyListeners = async () => {
    if (!isConnected.value || vehiclePropertyNames.length === 0) return;

    try {
      console.log("开始移除车辆属性监听器");

      for (const key in unsubscribeFunctions) {
        if (unsubscribeFunctions[key]) {
          await unsubscribeFunctions[key]();
          console.log(`已移除 ${key} 的监听器`);
        }
      }

      for (const key in unsubscribeFunctions) {
        delete unsubscribeFunctions[key];
      }

      for (const propertyName of vehiclePropertyNames) {
        await CarClient.unsubscribeProperty(propertyName);
      }

      console.log("车辆属性监听器移除完成");
    } catch (err) {
      console.error("移除车辆属性监听器失败:", err);
    }
  };

  const getProperty = async (
    propertyName: VehiclePropertyName,
    areaId: number = 0
  ) => {
    isLoading.value = true;
    error.value = null;

    try {
      const result = await CarClient.getProperty({ propertyName, areaId });
      return result;
    } catch (err) {
      console.error(`Failed to get property ${propertyName}:`, err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const getProperties = async (
    propertyNames: VehiclePropertyName[],
    areaId: number = 0
  ) => {
    isLoading.value = true;
    error.value = null;

    try {
      const result = await CarClient.getProperties({ propertyNames, areaId });
      return result.properties;
    } catch (err) {
      console.error("Failed to get properties:", err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const setProperty = async (
    propertyName: VehiclePropertyName,
    value: number | boolean | string,
    valueType: "int" | "float" | "boolean" = "int",
    areaId: number = 0
  ) => {
    isLoading.value = true;
    error.value = null;

    try {
      await CarClient.setProperty({
        propertyName,
        value,
        valueType,
        areaId,
      });
    } catch (err) {
      console.error(`Failed to set property ${propertyName}:`, err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const subscribeToProperty = async (
    propertyName: VehiclePropertyName,
    callback: (value: CarPropertyValue) => void,
    areaId: number = 0
  ) => {
    if (callbackCounts[propertyName] === undefined) {
      callbackCounts[propertyName] = 0;
    }
    if (callbackIntervalSum[propertyName] === undefined) {
      callbackIntervalSum[propertyName] = 0;
    }

    try {
      await CarClient.subscribeProperty(
        propertyName,
        areaId,
        (data: CarPropertyValue) => {
          const now = Date.now();
          calculateAndPrintAverageInterval(propertyName, now);

          if (data.type === "error") {
            console.error("[BOWEN_LOG] listener error:", data);
          } else {
            callback(data);
          }
        }
      );

      return async () => {
        await CarClient.unsubscribeProperty(propertyName);
      };
    } catch (err) {
      console.error(`Failed to subscribe to property ${propertyName}:`, err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    }
  };

  const unsubscribeToProperty = async (propertyName: VehiclePropertyName) => {
    isLoading.value = true;
    error.value = null;

    try {
      await CarClient.unsubscribeProperty(propertyName);
      console.log(`成功取消订阅属性: ${propertyName}`);
    } catch (err) {
      console.error(`取消订阅属性失败 ${propertyName}:`, err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const getMatrixVersions = async () => {
    isLoading.value = true;
    error.value = null;

    try {
      return await CarClient.getMatrixVersions();
    } catch (err) {
      console.error("Failed to get matrix versions:", err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const getVIN = async () => {
    isLoading.value = true;
    error.value = null;
    try {
      return await CarClient.getVIN();
    } catch (err) {
      console.error("Failed to get VIN:", err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const getVehicleInfo = async (infoId: VehicleInfoId) => {
    isLoading.value = true;
    error.value = null;

    try {
      const result = await CarClient.getVehicleInfo({ infoId });
      return result;
    } catch (err) {
      console.error(`获取车辆信息失败 ${infoId}:`, err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const getVehicleInfos = async (infoIds: VehicleInfoId[]) => {
    isLoading.value = true;
    error.value = null;

    try {
      const result = await CarClient.getVehicleInfos({ infoIds });
      return result.infos;
    } catch (err) {
      console.error("批量获取车辆信息失败:", err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  onMounted(async () => {
    await checkConnection();
    if (isConnected.value && vehiclePropertyNames.length > 0) {
      await removeVehiclePropertyListeners();
      await setupVehiclePropertyListeners();
    }
  });

  onUnmounted(async () => {
    await removeVehiclePropertyListeners();
    await CarClient.removeAllListeners();
  });

  return {
    isConnected,
    isLoading,
    error,
    vehicleProperties,
    checkConnection,
    getProperty,
    getProperties,
    setProperty,
    subscribeToProperty,
    unsubscribeToProperty,
    setupVehiclePropertyListeners,
    removeVehiclePropertyListeners,
    getMatrixVersions,
    VehiclePropertyNames,
    getVIN,
    getVehicleInfo,
    getVehicleInfos,
    VehicleInfoIds,
  };
}
